import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import {
    fetchCampaignContestByID,
    fetchUserCampaignContests,
    createUserCampaignContest,
} from '../../api/campaigns';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/LoadingSpinner';
import RewardDisplay from '../../components/RewardDisplay';
import ConstraintTags from '../../components/ConstraintTags';
import DifficultyBadge from '../../components/DifficultyBadge';

const CampaignContestDetailPage = () => {
    const [currentUser] = useAtom(currentUserAtom);
    const { campaignContestID } = useParams();
    const navigate = useNavigate();
    const [campaignContest, setCampaignContest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);
    const [hasActiveAttempt, setHasActiveAttempt] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [isMaxRewardsCapped, setIsMaxRewardsCapped] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchCampaignContestByID(campaignContestID);
                setCampaignContest(data);

                if (currentUser && data.campaignID) {
                    try {
                        const userContests = await fetchUserCampaignContests(data.campaignID);
                        const contestData = userContests.CampaignContests?.find(
                            ucc => String(ucc.campaignContestID) === String(campaignContestID)
                        );
                        const attempts = contestData?.UserCampaignContests || [];
                        const active = attempts.find(a => !a.result);
                        setHasActiveAttempt(!!active);
                        setIsLocked(contestData?.isLocked ?? false);
                        setIsMaxRewardsCapped(contestData?.isMaxRewardsCapped ?? false);
                    } catch (err) {
                        console.error('Failed to check active attempts:', err);
                    }
                }
            } catch (err) {
                setError('Error fetching campaign contest');
                toast.error('Error fetching campaign contest: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [campaignContestID, currentUser]);

    const handleStart = async () => {
        setStarting(true);
        try {
            const newUserCampaignContest = await createUserCampaignContest(campaignContestID);
            navigate(`/campaign-contests/${newUserCampaignContest.userCampaignContestID}/play`);
        } catch (err) {
            toast.error('Error starting campaign contest: ' + err.message);
        } finally {
            setStarting(false);
        }
    };


    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    if (!campaignContest) {
        return <div>No campaign contest found.</div>;
    }

    return (
        <div className="campaign-contest-detail-page">
            <div className="contest-header-row">
                <div className="contest-header-card">
                    <div className="banner-header">
                        <BackButton to={`/campaigns/${campaignContest.campaignID}`} />
                        <h2>{campaignContest.name}</h2>
                        {currentUser?.isAdmin && (
                            <button className="btn-secondary" onClick={() => navigate(`/campaign-contests/${campaignContestID}/edit`)}>Edit</button>
                        )}
                    </div>
                    {campaignContest.desc && <p className="contest-desc">{campaignContest.desc}</p>}
                    <DifficultyBadge difficulty={campaignContest.difficulty} />
                    {campaignContest.constraints?.length > 0 && (
                        <>
                            <ConstraintTags constraints={campaignContest.constraints} />
                            <Link
                                to={`/team/create?constraints=${encodeURIComponent(JSON.stringify(campaignContest.constraints))}`}
                                className="build-compliant-team-link"
                            >
                                <span className="build-compliant-team-link__icon">🔧</span>
                                <span className="build-compliant-team-link__text">Build a team for this challenge</span>
                            </Link>
                        </>
                    )}
                    {Array.isArray(campaignContest.links) && campaignContest.links.length > 0 && (
                        <div className="contest-links">
                            <h4>Related reading</h4>
                            <ul>
                                {campaignContest.links.map((link, i) => (
                                    <li key={i}>
                                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                                            {link.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {currentUser && !hasActiveAttempt && (
                        isMaxRewardsCapped
                            ? <span className="contest-completed-label">Completed</span>
                            : <button className="btn-primary" onClick={handleStart} disabled={starting || isLocked}>
                                {isLocked ? 'Locked' : starting ? 'Starting...' : 'Start'}
                            </button>
                    )}
                </div>
                <RewardDisplay reward={campaignContest.Reward} isClaimed={isMaxRewardsCapped} />
            </div>
        </div>
    );
};

export default CampaignContestDetailPage;
